import { chakra, Spinner, type HTMLChakraProps } from '@chakra-ui/react';
import { forwardRef, type ReactNode } from 'react';
import {
  buttonVariant,
  controlSize,
  focusRing,
  type ButtonVariant,
  type ControlSize,
} from './_shared';

const Base = chakra('button', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    position: 'relative',
    fontFamily: 'body',
    fontWeight: 'semibold',
    lineHeight: 'none',
    borderRadius: 'md',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    transitionProperty: 'background-color, border-color, color, filter, box-shadow',
    transitionDuration: 'fast',
    transitionTimingFunction: 'standard',
    _disabled: { opacity: 0.45, cursor: 'not-allowed', pointerEvents: 'none' },
    ...focusRing,
  },
});

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  /** Visual role. */
  variant?: ButtonVariant;
  size?: ControlSize;
  /** Show a spinner and block interaction while an action is in flight. */
  loading?: boolean;
  /** Optional label shown next to the spinner while `loading`. */
  loadingText?: ReactNode;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  /** Stretch to the width of the container. */
  fullWidth?: boolean;
  children?: ReactNode;
}

/**
 * The primary action primitive. Product API only — no Chakra style props leak out.
 * States: default / hover / focus-visible / active / disabled / loading.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    loadingText,
    iconStart,
    iconEnd,
    fullWidth,
    disabled,
    type = 'button',
    children,
    ...rest
  },
  ref,
) {
  const sx = { ...controlSize[size], ...buttonVariant[variant] } as HTMLChakraProps<'button'>;
  return (
    <Base
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      data-loading={loading || undefined}
      width={fullWidth ? 'full' : undefined}
      {...sx}
      {...rest}
    >
      {loading ? (
        <Spinner boxSize="1.1em" borderWidth="2px" color="currentColor" />
      ) : (
        iconStart && <chakra.span display="inline-flex" fontSize="1.15em">{iconStart}</chakra.span>
      )}
      {loading && loadingText ? loadingText : children}
      {!loading && iconEnd && (
        <chakra.span display="inline-flex" fontSize="1.15em">
          {iconEnd}
        </chakra.span>
      )}
    </Base>
  );
});
